/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useState } from 'react';
import { settingsApi } from '../../api';
import { useAuth } from '../../AuthContext';
import { RowMenu } from '../../components/SettingGroups';
import { IconChevronDown, IconDots, IconGrip, IconSave, IconSearch } from '../../components/icons';
import { ErrorNote, Modal, th, useLoader } from '../../components/common';
import { SettingsPage, Panel } from './shell';
import TherapistDialog from './TherapistDialog';

const STAFF_GROUPS = ['full_time', 'daily', 'part_time'];

const serviceName = (service) =>
  [service.name_en, service.name_th].filter(Boolean).join(' ') || service.code;

const commissionAmount = (variant) =>
  Number(variant.commission ?? variant.baseCommission ?? 0).toLocaleString('en-US', {
    maximumFractionDigits: 2,
  });

/**
 * The original separates therapist payouts from customer pricing:
 * ตั้งค่า → หมอนวด → ค่ามือหมอนวด. Each active branch owns one fixed amount
 * per service duration, while the therapist list itself remains organization-wide.
 */
function CommissionSettings({ data, search, onReload }) {
  const [editing, setEditing] = useState(null);
  const q = search.trim().toLowerCase();
  const visible = (data.services || []).filter((service) =>
    service.is_active
    && (service.variants || []).some((variant) => variant.isActive !== false)
    && (!q || serviceName(service).toLowerCase().includes(q)));

  const grouped = visible.reduce((groups, service) => {
    const name = service.category_name_en
      || service.category_name_th
      || service.category_name
      || 'ไม่มีกลุ่ม';
    (groups[name] ||= []).push(service);
    return groups;
  }, {});

  return (
    <>
      <div className="commission-settings">
        {Object.entries(grouped).map(([category, services]) => (
          <section className="commission-category" key={category}>
            <h2>{category}</h2>
            <div className="commission-service-list">
              {services.map((service) => {
                const variants = service.variants
                  .filter((variant) => variant.isActive !== false)
                  .sort((a, b) => a.durationMinutes - b.durationMinutes);
                return (
                  <div className="commission-service-row" key={service.id}>
                    <span className="commission-service-photo">
                      {service.image_url ? <img src={service.image_url} alt="" /> : null}
                    </span>
                    <span className="commission-service-name">
                      <strong>{serviceName(service)}</strong>
                      <small>ค่ามือเท่ากันทุกคน</small>
                    </span>
                    <span className="commission-mode"><b>฿</b> จำนวนเงิน</span>
                    <span className="commission-values">
                      {variants.map((variant) => (
                        <span key={variant.id}>
                          <small>{variant.durationMinutes} นาที</small>
                          <strong>{commissionAmount(variant)}</strong>
                        </span>
                      ))}
                    </span>
                    <button
                      type="button"
                      className="commission-edit"
                      title="แก้ไขค่ามือ"
                      onClick={() => setEditing({ ...service, variants })}
                    >
                      <IconDots size={19} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        {!visible.length && <div className="staff-empty">ไม่พบบริการ</div>}
      </div>

      {editing && (
        <CommissionDialog
          service={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onReload();
          }}
        />
      )}
    </>
  );
}

function CommissionDialog({ service, onClose, onSaved }) {
  const [rows, setRows] = useState(() => service.variants.map((variant) => ({
    variantId: variant.id,
    durationMinutes: variant.durationMinutes,
    amount: String(variant.commission ?? variant.baseCommission ?? 0),
  })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    const commissions = rows.map((row) => ({
      variantId: row.variantId,
      amount: Number(row.amount),
    }));
    if (commissions.some((row) => !Number.isFinite(row.amount) || row.amount < 0)) {
      setError(new Error('จำนวนค่ามือไม่ถูกต้อง'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await settingsApi.updateServiceCommissions(service.id, commissions);
      onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`แก้ไขค่ามือ — ${serviceName(service)}`}
      onClose={onClose}
      className="commission-modal"
      footer={(
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button className="btn-solid" onClick={save} disabled={busy}>
            <IconSave size={16} /> {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      )}
    >
      <ErrorNote error={error} />
      <div className="commission-dialog-note">
        <strong>ค่ามือเท่ากันทุกคน</strong>
        <span>จำนวนเงินคงที่ของสาขาปัจจุบัน แยกตามระยะเวลาบริการ</span>
      </div>
      <div className="commission-edit-list">
        {rows.map((row, index) => (
          <label className="commission-edit-row" key={row.variantId}>
            <span>{row.durationMinutes} นาที</span>
            <span className="commission-money-input">
              <b>฿</b>
              <input
                type="number"
                min="0"
                step="1"
                value={row.amount}
                onChange={(event) => setRows((current) => current.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, amount: event.target.value } : item))}
              />
            </span>
          </label>
        ))}
      </div>
    </Modal>
  );
}

export function SettingStaff() {
  const { branches } = useAuth();
  const [tab, setTab] = useState('list');
  const [branchScope, setBranchScope] = useState('all');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [commissionSearch, setCommissionSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const therapists = useLoader(
    () => settingsApi.therapists({
      includeInactive: true,
      scope: branchScope === 'all' ? 'all' : 'branch',
      branchId: branchScope === 'all' ? undefined : branchScope,
    }),
    [branchScope],
  );
  // The dialog's ประเภทบริการเฉพาะ list is the catalogue, which is not this
  // page's own data — the same way the บริการ dialog borrows its lookups.
  const services = useLoader(() => settingsApi.services(), []);

  const toggle = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  return (
    <SettingsPage
      tab={tab}
      onTab={setTab}
      action={tab === 'list' ? 'สร้างหมอนวด' : null}
      onAction={() => setCreating(true)}
      tabs={[{ key: 'list', label: 'หมอนวด' }, { key: 'pay', label: 'ค่ามือหมอนวด' }]}
      search={(
        <div className="flex items-center gap-2">
          {tab === 'list' && (
            <>
              <select
                className="chip"
                value={branchScope}
                onChange={(e) => setBranchScope(e.target.value)}
                aria-label="เลือกสาขาหมอนวด"
              >
                <option value="all">ทุกสาขา</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>สาขา {b.code}</option>
                ))}
              </select>
              <label className="search-field">
                <IconSearch size={17} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="ค้นหาหมอนวด"
                />
              </label>
            </>
          )}
          {tab === 'pay' && (
            <label className="search-field">
              <IconSearch size={17} />
              <input
                value={commissionSearch}
                onChange={(e) => setCommissionSearch(e.target.value)}
                placeholder="ค้นหาบริการ"
              />
            </label>
          )}
        </div>
      )}
    >
      <Panel
        loader={tab === 'pay' ? services : therapists}
        empty={(d) => (tab === 'pay' ? !d.services.length : !d.therapists.length)}
        emptyIcon={tab === 'pay' ? 'treatment' : 'staff'}
      >
        {(d) => {
          if (tab === 'pay') {
            return (
              <CommissionSettings
                data={d}
                search={commissionSearch}
                onReload={services.reload}
              />
            );
          }

          const q = search.trim().toLowerCase();
          const match = (t) => !q
            || `${t.nickname_en || ''} ${t.nickname_th || ''} ${t.code || ''}`.toLowerCase().includes(q);

          const working = d.therapists.filter((t) => t.is_active && match(t));
          // Everyone who has left or been stood down, kept out of the groups but
          // still reachable — the original gives them their own section.
          const retired = d.therapists.filter((t) => !t.is_active && match(t));

          const row = (t) => (
            <div className="staff-row" key={t.id}>
              <span className="grip"><IconGrip size={18} /></span>
              <span className="staff-avatar">
                {t.photo_url ? <img src={t.photo_url} alt="" /> : null}
              </span>
              {/* The name is the row's own control on their page — the ••• menu
                  offers แก้ไข as well, but nobody reaches for it. */}
              <button type="button" className="staff-name row-open" onClick={() => setEditing(t)}>
                {t.nickname_en}{t.nickname_th ? ` (${t.nickname_th})` : ''}
              </button>
              <RowMenu
                items={[
                  { label: 'แก้ไข', onClick: () => setEditing(t) },
                  t.is_active
                    ? { label: 'พักงาน', danger: true,
                        onClick: async () => { await settingsApi.remove('therapists', t.id); therapists.reload(); } }
                    : { label: 'กลับมาทำงาน',
                        onClick: async () => { await settingsApi.restore('therapists', t.id); therapists.reload(); } },
                ]}
              />
            </div>
          );

          return (
            <>
              {STAFF_GROUPS.map((g) => {
                const items = working.filter((t) => t.employment_type === g);
                return (
                  <div className="setting-group staff-group" key={g}>
                    {/* Translated from their markup:
                        <div class="w-full flex items-center justify-between">
                          <div class="ml-2 flex items-center">
                            <span font-weight="500" font-size="1.25rem">…</span>
                        The 16px inset is the card's own padding, measured on
                        their /setting-staff, so the header adds none. */}
                    <div className="w-full flex items-center justify-between">
                      <div className="ml-2 flex items-center gap-2">
                        <span className="group-title">{th(g)}</span>
                        <span className="group-count">({items.length} คน)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="w-[32px] h-[32px] cursor-pointer bg-gray-50 rounded-sm grid place-items-center border-0 text-gray-500"
                          title={collapsed[g] ? 'ขยาย' : 'ย่อ'}
                          onClick={() => toggle(g)}
                        >
                          <IconChevronDown size={18} className={collapsed[g] ? '' : 'flip'} />
                        </button>
                      </div>
                    </div>
                    {!collapsed[g] && (
                      items.length ? <div className="staff-list">{items.map(row)}</div>
                        : <div className="staff-empty">ไม่มีหมอนวดในกลุ่มนี้</div>
                    )}
                  </div>
                );
              })}

              {/* Same card as the groups above it, rather than the padded,
                  ruled `.group-head` this section used to borrow from ค่ามือ —
                  on their page the two look identical. */}
              <div className="setting-group staff-group">
                <div className="w-full flex items-center justify-between">
                  <div className="ml-2 flex items-center gap-2">
                    <span className="group-title">หมอนวดพักงานหรือเลิกจ้าง</span>
                    <span className="group-count">({retired.length} คน)</span>
                  </div>
                  <button
                    className="w-[32px] h-[32px] cursor-pointer bg-gray-50 rounded-sm grid place-items-center border-0 text-gray-500"
                    title={collapsed.retired ? 'ขยาย' : 'ย่อ'}
                    onClick={() => toggle('retired')}
                  >
                    <IconChevronDown size={18} className={collapsed.retired ? '' : 'flip'} />
                  </button>
                </div>
                {!collapsed.retired && (
                  retired.length ? <div className="staff-list">{retired.map(row)}</div>
                    : <div className="staff-empty">ไม่มีรายการ</div>
                )}
              </div>
            </>
          );
        }}
      </Panel>

      {(creating || editing) && (
        <TherapistDialog
          therapist={editing}
          services={(services.data?.services || []).filter((s) => s.is_active && !s.is_addon)}
          onSubmit={async (payload) => {
            if (editing) await settingsApi.update('therapists', editing.id, payload);
            else await settingsApi.createTherapist(payload);
            therapists.reload();
            setCreating(false);
            setEditing(null);
          }}
          onClose={() => { setCreating(false); setEditing(null); }}
        />
      )}
    </SettingsPage>
  );
}

/* ── ห้องนวด ─────────────────────────────────────────────────────────── */
